import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ChevronDown, LockKeyhole, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { MarkdownLite } from "@/lib/markdown-lite";
import {
  canEditRoundContent,
  formatRoundsTime,
  isIdentifierGuardError,
  roundsErrorMessage,
  useEditRoundPost,
  type RoundPost,
} from "@/lib/rounds";
import { ReplyComposer } from "@/components/rounds/ReplyComposer";
import { ReportMenu } from "@/components/rounds/ReportMenu";
import { RoleChip } from "@/components/rounds/ThreadRow";
import { VoteButton } from "@/components/rounds/VoteButton";

interface PostNode {
  post: RoundPost;
  children: PostNode[];
}

export function PostTree({
  citySlug,
  threadId,
  posts,
  currentHandle,
  locked,
}: {
  citySlug: string;
  threadId: string;
  posts: RoundPost[];
  currentHandle?: string;
  locked: boolean;
}) {
  const roots = useMemo(() => buildPostTree(posts), [posts]);

  if (!roots.length) {
    return <p className="pb-rounds-empty">No replies yet. Start the discussion.</p>;
  }

  return (
    <div className="pb-rounds-post-tree">
      {roots.map((node) => (
        <PostNodeView
          key={node.post.id}
          node={node}
          citySlug={citySlug}
          threadId={threadId}
          currentHandle={currentHandle}
          locked={locked}
        />
      ))}
    </div>
  );
}

function PostNodeView({
  node,
  citySlug,
  threadId,
  currentHandle,
  locked,
}: {
  node: PostNode;
  citySlug: string;
  threadId: string;
  currentHandle?: string;
  locked: boolean;
}) {
  const { post } = node;
  const editPost = useEditRoundPost(citySlug, threadId, post.id);
  const [replying, setReplying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(post.body);
  const [expanded, setExpanded] = useState(post.depth < 2);
  const canEdit =
    post.status === "published" &&
    canEditRoundContent(post.createdAt, post.author.handle, currentHandle);
  const identifierError = isIdentifierGuardError(editPost.error);
  const hiddenReplyCount = countNodes(node.children);

  useEffect(() => setEditBody(post.body), [post.body]);

  const submitEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editBody.trim()) return;
    editPost.mutate(
      { body: editBody.trim() },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="pb-rounds-post-branch">
      <article className="pb-rounds-post" data-depth={Math.min(post.depth, 5)}>
        <header>
          <span className="pb-rounds-author">
            <strong>{post.author.display}</strong>
            <small>@{post.author.handle}</small>
            <RoleChip role={post.author.role} />
            {post.author.userId ? (
              <Link className="pb-rounds-message-link" to={`/board/messages/new?handle=${encodeURIComponent(post.author.handle)}`}>
                <MessageCircle size={11} aria-hidden="true" />Message
              </Link>
            ) : null}
          </span>
          <time dateTime={post.createdAt}>{formatRoundsTime(post.createdAt)}</time>
        </header>

        {post.status !== "published" ? (
          <span className="pb-rounds-content-status">{post.status.replace("_", " ")}</span>
        ) : null}

        {editing ? (
          <form className="pb-rounds-edit-form" onSubmit={submitEdit}>
            <label htmlFor={`edit-post-${post.id}`}>Edit reply</label>
            <textarea
              id={`edit-post-${post.id}`}
              maxLength={4000}
              required
              value={editBody}
              onChange={(event) => {
                setEditBody(event.target.value);
                if (editPost.isError) editPost.reset();
              }}
            />
            {identifierError ? (
              <p className="pb-rounds-identifier-error" role="alert">
                Remove patient-identifying details before saving. <Link to="/board/rounds/guidelines">Read the case-writing guidelines.</Link>
              </p>
            ) : editPost.isError ? (
              <p className="pb-rounds-form-error" role="alert">{roundsErrorMessage(editPost.error)}</p>
            ) : null}
            <footer>
              <span>{editBody.length.toLocaleString("en-AU")} / 4,000</span>
              <div>
                <button type="button" className="pb-rounds-plain-button" onClick={() => setEditing(false)}>Cancel</button>
                <button className="pb-rounds-primary" type="submit" disabled={editPost.isPending || !editBody.trim()}>
                  {editPost.isPending ? "Saving…" : "Save edit"}
                </button>
              </div>
            </footer>
          </form>
        ) : (
          <MarkdownLite>{post.body}</MarkdownLite>
        )}

        <footer className="pb-rounds-post__actions">
          <VoteButton
            citySlug={citySlug}
            threadId={threadId}
            targetType="post"
            targetId={post.id}
            score={post.score}
            myVote={post.myVote}
          />
          {!locked && post.depth < 5 ? (
            <button type="button" onClick={() => setReplying((current) => !current)}>
              Reply
            </button>
          ) : null}
          {canEdit ? <button type="button" onClick={() => setEditing(true)}>Edit</button> : null}
          <ReportMenu citySlug={citySlug} targetType="post" targetId={post.id} />
          {post.editedAt ? <small>Edited {formatRoundsTime(post.editedAt)}</small> : null}
        </footer>

        {replying ? (
          <ReplyComposer
            citySlug={citySlug}
            threadId={threadId}
            parentPostId={post.id}
            placeholder={`Reply to ${post.author.display}…`}
            onCancel={() => setReplying(false)}
            onPosted={() => setReplying(false)}
          />
        ) : null}
      </article>

      {node.children.length ? (
        expanded ? (
          <div className="pb-rounds-post-children">
            {node.children.map((child) => (
              <PostNodeView
                key={child.post.id}
                node={child}
                citySlug={citySlug}
                threadId={threadId}
                currentHandle={currentHandle}
                locked={locked}
              />
            ))}
          </div>
        ) : (
          <button className="pb-rounds-expand-replies" type="button" onClick={() => setExpanded(true)}>
            <ChevronDown size={13} aria-hidden="true" />
            Show {hiddenReplyCount} deeper {hiddenReplyCount === 1 ? "reply" : "replies"}
          </button>
        )
      ) : null}
      {locked && post.depth === 0 ? <span className="pb-rounds-lock-note"><LockKeyhole size={12} aria-hidden="true" />Replies are closed</span> : null}
    </div>
  );
}

function buildPostTree(posts: RoundPost[]): PostNode[] {
  const nodes = new Map<string, PostNode>();
  for (const post of posts) nodes.set(post.id, { post, children: [] });

  const roots: PostNode[] = [];
  for (const post of posts) {
    const node = nodes.get(post.id);
    if (!node) continue;
    const parent = post.parentPostId ? nodes.get(post.parentPostId) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

function countNodes(nodes: PostNode[]): number {
  return nodes.reduce((total, node) => total + 1 + countNodes(node.children), 0);
}
